import * as moment from 'moment';
import {Component, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AssetsService} from '../_services/assets.service';
import {AssetModel} from '../_models/asset.model';
import {PlantRiskService} from '../_services/plant-risk.service';
import {SoService} from '../../shared/_services/so.service';
import {LoadingService} from '../../shared/_services/loading.service';
import {LsService} from '../../shared/_services/ls.service';
import {SwiperConfigInterface} from 'ngx-swiper-wrapper';
import {DateService} from '../_services/date.service';
import {DownloadService} from '../_services/download.service';

class AssetSwiperCfg {
  idx: number;
  cfg: SwiperConfigInterface;
}

@Component({
  selector: 'app-intramonth',
  templateUrl: './im.component.html',
  styleUrls: ['./im.component.scss']
})

export class ImComponent implements OnDestroy {

  curDate = '';

  assets: AssetModel[];
  isAssetSoActive = false;
  isLoading = false;

  private readonly LS_OUTAGE_TABLE = 'withOutageTable';
  withOutageTable = this.lsService.getItem(this.LS_OUTAGE_TABLE) ? this.lsService.getItem(this.LS_OUTAGE_TABLE) === 'true' : false;

  private readonly LS_GRID_VIEW = 'withGridView';
  withGridView = this.lsService.getItem(this.LS_GRID_VIEW) ? this.lsService.getItem(this.LS_GRID_VIEW) === 'true' : false;

  // SWIPER
  swiperBaseHeight = 414;

  countrySwiperConfig: SwiperConfigInterface = {
    noSwiping: true,
    noSwipingClass: 'slide-noswiping',
    observer: true,
    spaceBetween: 40,
    speed: 400,
  };
  countrySwiperIdx = 0;

  assetSwiperConfigs: AssetSwiperCfg[] = [];

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private assetsService: AssetsService,
    private dateService: DateService,
    private downloadService: DownloadService,
    private plantRiskService: PlantRiskService,
    private loadingService: LoadingService,
    private lsService: LsService,
    private soService: SoService,
  ) {
    this.getDate();
    this.getSo();
    this.getLoading();
    this.getPr();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getDate(): void {
    this.dateService.onNewDate()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (date) => {
          if (date) {
            this.curDate = date.date;
          }
        }
      );
  }

  private getSo(): void {
    this.soService.onNewSoActive()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (so) => {
          this.isAssetSoActive = so === 'assets';
        }
      );
  }

  private getLoading(): void {
    this.loadingService.onNewLoading()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (loading) => {
          this.isLoading = loading?.length > 0;
        }
      );
  }

  private getPr(): void {
    this.plantRiskService.onNewPlantRisk()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (im) => {
          if (im?.length) {
            this.assets = this.assetsService.getAssets();
            // set countrySwiperIdx to first country that has any selected assets
            this.countrySwiperIdx = 0;
            for (let i = 0; i < this.assets.length; i++) {
              if (this.assets[i].assetsSelected.length) {
                this.countrySwiperIdx = i;
                break;
              }
            }
            this.setSwiperConfigs();
          } else {
            // reset assets when im data is reset, to clear the screen while user is making new selections
            this.assets = [];
          }
        }
      );
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  // ********************************************************************************************************
  // UI
  // ********************************************************************************************************

  goToCountrySlide(idx: number): void {
    this.countrySwiperIdx = idx;
  }

  goToAssetSlide(countryIdx: number, idx: number): void {
    this.assetSwiperConfigs[countryIdx].idx = idx;
  }

  onToggleOutageTable(): void {
    this.withOutageTable = !this.withOutageTable;
    this.lsService.setItem(this.LS_OUTAGE_TABLE, this.withOutageTable);
  }

  onToggleAssetSwiper(): void {
    this.withGridView = !this.withGridView;
    this.lsService.setItem(this.LS_GRID_VIEW, this.withGridView);
    this.setSwiperConfigs();
  }

  private setSwiperConfigs(): void {
    this.assetSwiperConfigs = [];
    if (this.assets?.length) {
      for (const country of this.assets) {
        const cfg = {
          noSwiping: true,
          noSwipingClass: 'slide-noswiping',
          observer: true,
          slidesPerColumn: 1,
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 400,
        };
        if (this.withGridView) {
          cfg.slidesPerView = 2;
          cfg.slidesPerColumn = Math.ceil(country.assetsSelected.length / 2);
        }
        this.assetSwiperConfigs.push({
          idx: 0,
          cfg: cfg,
        });
      }
    }
  }

  get moment() {
    return moment;
  }

  onDownload(country: string, type: 'all' | 'assets' | 'mf' | 'pos' | 'oc'): void {
    switch (type) {
      case 'all':
        this.downloadService.downloadAll();
        return;
      case 'assets':
        this.downloadService.downloadAssets(country);
        return;
      case 'mf':
        this.downloadService.downloadMf(country);
        return;
      case 'pos':
        this.downloadService.downloadPos(country);
        return;
      case 'oc':
        this.downloadService.downloadOc(country);
        return;
    }
  }

}
